import { getTokenLS } from "../lib/token";
import { getOrderId } from "../lib/orderId";
import { fotoUrl } from "../lib/fotoUrl";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import FotoBlobImg from "./FotoBlobImg";
import { useFormData } from "../context/FormDataContext";
import { apiFetch, API_BASE, getToken } from "../auth/authClient";


function fotoThumbSrc(m, orderId){
  const token = (()=>{ try { return (typeof getToken==="function" ? (getToken()||"") : (getToken()||"")); } catch { return ""; } })();
  const qs = new URLSearchParams();
  if (m?.url && typeof m.url === "string") {
    // m.url je typicky "/api/foto/file?orderId=...&kategorie=...&file=..."
    const u = m.url.includes("?") ? (m.url + "&") : (m.url + "?");
    return u + "token=" + encodeURIComponent(token);
  }
  qs.set("orderId", String(orderId||""));
  if (m?.kategorie) qs.set("kategorie", String(m.kategorie));
  if (m?.file) qs.set("file", String(m.file));
  qs.set("token", token);
  return "/api/foto/file?" + qs.toString();
}
// ===== FOTO_GEO_UPLOAD_V1 =====
async function getGeoOnce(opts = {}) {
  try {
    if (typeof navigator === "undefined" || !navigator.geolocation) return null;
    const enableHighAccuracy = opts.enableHighAccuracy ?? true;
    const timeout = opts.timeout ?? 7000;       // 7s
    const maximumAge = opts.maximumAge ?? 60000; // 60s cache

    return await new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = pos && pos.coords ? pos.coords : null;
          if (!c) return resolve(null);
          resolve({
            lat: c.latitude,
            lon: c.longitude,
            acc: (typeof c.accuracy === "number") ? c.accuracy : null,
            source: "browser",
          });
        },
        () => resolve(null),
        { enableHighAccuracy, timeout, maximumAge }
      );
    });
  } catch {
    return null;
  }
}
// ===== /FOTO_GEO_UPLOAD_V1 =====


const normalizeUploadFile = (raw) => {
  if (!raw) return null;
  const name = (raw && raw.name && String(raw.name).trim()) ? String(raw.name) : `photo_${Date.now()}.jpg`;
  const type = (raw && raw.type && String(raw.type).trim()) ? String(raw.type) : "image/jpeg";
  try {
    return new File([raw], name, { type });
  } catch (e) {
    // fallback – když File constructor zlobí (starší webview)
    raw.name = name;
    return raw;
  }
};


const cardStyle = { border: "1px solid #333", borderRadius: 12, padding: 12, marginBottom: 12 };
const btnStyle = { padding: "10px 14px", borderRadius: 10, border: "1px solid #444", cursor: "pointer", fontWeight: 900 };
const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #444", outline: "none" };
const labelStyle = { fontSize: 13, fontWeight: 800, marginBottom: 6 };

function safeStr(v) {
  return v == null ? "" : String(v);
}
function safePart(v) {
  return safeStr(v).trim().replace(/[\\/:*?"<>|]+/g, "_").replace(/\s+/g, "_");
}

// fallback ID jen když fakt nemáš ID zakázky z knihy
function makeOrderId(formData) {
  const zes = formData?.zesnulaOsoba || {};
  const vst = formData?.vstupniInformace || {};
  const j = safePart(zes?.jmeno || vst?.jmenoZesnuleho || "");
  const p = safePart(zes?.prijmeni || vst?.prijmeniZesnuleho || "");
  const dn = safePart(zes?.datumNarozeni || vst?.datumNarozeniZesnuleho || "");
  const du = safePart(vst?.datumUmrti || zes?.datumUmrti || "");
  const out = [p, j, dn, du].filter(Boolean).join("_");
  return out || "";
}

function isImageName(name) {
  return /\.(png|jpe?g|webp|gif)$/i.test(name || "");
}

export default function FotoDokumentace() {
  const HTTPS_API_BASE = "";
  // === AUTO: safe helpers ===
  function asArray(x) { return Array.isArray(x) ? x : []; }
  // === /AUTO: safe helpers ===


  const ctx = useFormData();
    const { formData, currentOrderId } = ctx;

  const [kategorie, setKategorie] = useState("ohledaci_list");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [items, setItems] = useState([]);

  const orderId = useMemo(() => {
    // ✅ Priorita je klíčová:
    // 1) currentOrderId z kontextu (to je pravda po otevření zakázky)
    // 2) formData.zakazkaId (pokud je uložené v knize)
    // 3) localStorage lastOpenedOrderId (nouzovka po refreshi)
    // ❌ NEbereme formData.orderId (může zůstat staré a lepit se na 114)
    const cid = safeStr(currentOrderId).trim();
    if (cid) return cid;

    const zid = safeStr(formData?.zakazkaId).trim() || safeStr(formData?.id).trim();
    if (zid) return zid;

    let fromLS = "";
    try { fromLS = localStorage.getItem("lastOpenedOrderId") || ""; } catch (e) {}
    fromLS = safeStr(fromLS).trim();
    return fromLS || "";
  }, [formData, currentOrderId]);



  // ===============================
  // ✅ loadList = načti seznam fotek ze serveru (MUSÍ být ve scope komponenty)
  // ===============================
  const loadList = useCallback(async () => {
    if (!orderId || !String(orderId).trim()) return;

    setBusy(true);
    setMsg("");
    try {
      const r = await apiFetch(`/api/foto/list?orderId=${encodeURIComponent(String(orderId))}`);
      const files = r?.files || r?.items || r?.data?.files || [];
      setItems(Array.isArray(files) ? files : []);
    } catch (e) {
      setMsg(`❌ Načtení seznamu selhalo: ${safeStr(e?.data?.error || e?.message || e)}`);
    } finally {
      setBusy(false);
    }
  }, [orderId]);

  // načti seznam při změně zakázky
  useEffect(() => {
    if (!orderId) return;
    loadList();
  }, [orderId, loadList]);


  const [lightbox, setLightbox] = useState(null); 
// map: "kategorie/file" => { url, mime, size }
  const [previews, setPreviews] = useState({});
// ✅ primárně ber ID z knihy / aktuální zakázky

  // === AUTO: guard orderId ===
  if (!orderId) {
    return (
      <div style={{ padding: 12 }}>
        Nejdřív otevři / ulož zakázku (aby existovalo Zakázka ID). Bez orderId nejde fotodokumentace.
      </div>
    );
  }
  // === /AUTO: guard orderId ===



  



  

    // ===== FOTO_META_UI_V1 =====
    const [metaByKey, setMetaByKey] = useState({});
    const fetchMeta = useCallback(async (kat, file) => {
      try {
        if (!orderId || !file) return null;
        const key = `${kat}/${file}`;
        if (metaByKey[key] !== undefined) return metaByKey[key]; // null je validni (nema meta)
        const r = await apiFetch(
          `/api/foto/meta?orderId=${encodeURIComponent(orderId)}&kategorie=${encodeURIComponent(kat)}&file=${encodeURIComponent(file)}`
        );
        const meta = r?.meta ?? null;
        setMetaByKey((prev) => ({ ...(prev || {}), [key]: meta }));
        return meta;
      } catch (e) {
        console.warn("fetchMeta failed", kat, file, e);
        const key = `${kat}/${file}`;
        setMetaByKey((prev) => ({ ...(prev || {}), [key]: null }));
        return null;
      }
    }, [orderId, metaByKey]);
    // ===== /FOTO_META_UI_V1 =====


const zipLabel = useMemo(() => {
    const zes = formData?.zesnulaOsoba || {};
    const vst = formData?.vstupniInformace || {};

    const j = safePart(zes?.jmeno || vst?.jmenoZesnuleho || "");
    const p = safePart(zes?.prijmeni || vst?.prijmeniZesnuleho || "");

    // datum narození – ber z obou míst a projisti safePart
    const dn = safePart(
      zes?.datumNarozeni ||
      vst?.datumNarozeniZesnuleho ||
      zes?.datNarozeni ||
      vst?.datNarozeniZesnuleho ||
      ""
    );

    const oid = safePart(orderId || "");

    const parts = [p, j, dn, oid].filter(Boolean);
    return parts.length ? parts.join("_") : (orderId || "zakazka");
  }, [formData, orderId]);
const hasRealId = useMemo(() => {
    // TRUE pokud máme jakékoliv ID, se kterým API fotek umí pracovat
    return Boolean(
      safeStr(orderId).trim() ||
      safeStr(currentOrderId).trim() ||
      safeStr(formData?.zakazkaId).trim() ||
      safeStr(formData?.orderId).trim() ||
      safeStr(formData?.id).trim()
    );
  }, [formData, currentOrderId, orderId]);
  const stahnoutZip = useCallback(async (katOpt) => {
    try {
      if (!hasRealId) {
        alert("Open/load an order in Kniha first (missing order ID).");
        return;
      }

      setBusy(true);
      setMsg("");

      const token =
        getToken() ||
        "";

      const url = `${HTTPS_API_BASE}/api/foto/zip?orderId=${encodeURIComponent(orderId)}`;

      const res = await fetch(url, {
        method: "GET",
        headers: { ...authHeaders() },
          }
        );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);

      setPreviews((prev) => {
        // kdyby mezitím někdo přepsal, tak uvolni nově vytvořený url
        if (prev[key]?.url) {
          try { URL.revokeObjectURL(objUrl); } catch {}
          return prev;
        }
        return { ...prev, [key]: { url, mime: blob.type, size: blob.size } };
      });
    } catch (e) {
      // preview je best-effort – nechceme tím shodit celou obrazovku
      console.warn("Preview failed", kat, file, e);
    }
  }, [orderId, previews]);

  
    // === FIX: fetchPreview (blob -> objectURL, protože img neumí Authorization header) ===
    const previewKeysRef = useRef(new Set());

    const fetchPreview = useCallback(async (kat, file) => {
      if (!orderId || !file) return;

      const k = String(kat || "jine");
      const f = String(file || "");
      const key = `${k}/${f}`;

      if (previewKeysRef.current.has(key)) return;
      previewKeysRef.current.add(key);

      try {
        const token =
          getToken() ||
          getToken() ||
          "";
          const base = HTTPS_API_BASE;
        const url =
          `${base}/api/foto/file` +
          `?orderId=${encodeURIComponent(String(orderId))}` +
          `&kategorie=${encodeURIComponent(k)}` +
          `&file=${encodeURIComponent(f)}`;

        const r = await fetch(url, {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!r.ok) {
          previewKeysRef.current.delete(key); // ať lze retry
          return;
        }

        const blob = await r.blob();
        const objUrl = URL.createObjectURL(blob);

        setPreviews((prev) => {
          const cur = prev || {};
          if (cur[key]) {
            try { URL.revokeObjectURL(objUrl); } catch {}
            return cur;
          }
          return { ...cur, [key]: { url: objUrl } };
        });
      } catch (e) {
        previewKeysRef.current.delete(key);
      }
    }, [orderId, setPreviews]);

    // po načtení listu si postupně natahej náhledy pro obrázky
  useEffect(() => {
    let cancelled = false;
  const preloadPreviews = async () => {
      if (!orderId) return;
      const imgs = (items || []).filter((it) => isImageName(it?.file || it?.filename));
      for (const it of imgs) {
        if (cancelled) return;
        const kat = it?.kategorie || "jine";
        const file = it?.file || it?.filename;
        if (file) {
          // neblokuj UI úplně – jen postupně
          // eslint-disable-next-line no-await-in-loop
          await fetchPreview(kat, file);
                    await fetchMeta(kat, file);
}
      }
    };
      preloadPreviews();
    return () => {
      cancelled = true;
    };
  }, [items, orderId, fetchPreview]);

  const handleUpload = async (e) => {
    const list = e?.target?.files ? Array.from(e.target.files) : [];
    if (!list.length) return;

    setBusy(true);
    setMsg("");

    try {
      if (!hasRealId) {
        setMsg("❌ Nejdřív otevři/načti zakázku v Knize (chybí ID zakázky). Pak lze nahrávat fotky.");
        return;
      }

      // server bere 1 soubor pod klíčem "files" (multipart) – u tebe na VPS to tak bylo v curl
      // (pokud by server bral "file", dej vědět – upravím)
      if (!orderId || !String(orderId).trim()) {
          alert("Nejdřív otevři zakázku v Knize (chybí orderId).");
          return;
        }

        for (const f of list) {
          const fd = new FormData();
          fd.append("orderId", String(orderId));
          fd.append("kategorie", String(kategorie || "jine"));
// FOTO_GEO_UPLOAD_V1: posli datum/cas + GPS (pokud je dostupna)
  const takenAt = new Date().toISOString();
  fd.append("takenAt", takenAt);
  const geo = await getGeoOnce();
  if (geo && geo.lat != null && geo.lon != null) {
    fd.append("lat", String(geo.lat));
    fd.append("lon", String(geo.lon));
    if (geo.acc != null) fd.append("acc", String(geo.acc));
  }

        fd.append("files", f);

        await apiFetch("/api/foto/upload", {
          method: "POST",
          body: fd,
        });
      }

      setMsg("✅ Nahráno.");
      await loadList();
    } catch (e2) {
      setMsg(`❌ Upload selhal: ${safeStr(e2?.data?.error || e2?.message || e2)}`);
    } finally {
      setBusy(false);
      try {
        e.target.value = "";
      } catch {}
    }
  };

    // ===== FOTO_SET_VYJEZD_V1 =====
    const setAsVyjezdLocation = async (it) => {
      try {
        const file = it?.filename || it?.file;
        const kat = it?.kategorie || "jine";
        if (!orderId || !file) return;

        const key = `${kat}/${file}`;
        const meta = metaByKey[key] !== undefined ? metaByKey[key] : await fetchMeta(kat, file);

        if (!meta) {
          alert("U téhle fotky nemám meta (čas/GPS). Nahraj ji znovu nebo zkontroluj povolení polohy.");
          return;
        }

        const addr = meta?.address || meta?.addr || null;
        const geo = meta?.geo || null;

         // FOTO_ADDR_UI_V1: pokud neni adresa, zkus dohledat ze serveru z GPS
         let resolvedAddr = addr;
         if (!resolvedAddr && geo && geo.lat != null && geo.lon != null) {
           try {
             const rr = await apiFetch(`/api/geo/reverse?lat=${encodeURIComponent(String(geo.lat))}&lon=${encodeURIComponent(String(geo.lon))}`);
             const a = rr?.address || null;
             if (a) {
               resolvedAddr = a;
               // uloz do meta cache, at se to hned zobrazi i pod fotkou
               const key2 = `${kat}/${file}`;
               const merged = { ...(meta || {}), address: a };
               setMetaByKey((prev) => ({ ...(prev || {}), [key2]: merged }));
             }
           } catch {}
         }


        if (!addr && !(geo && geo.lat != null && geo.lon != null)) {
          alert("Meta nemá adresu ani souřadnice.");
          return;
        }

        const ok = confirm(
          `Nastavit jako místo výjezdu?\n\n` +
          `${resolvedAddr ? ("Adresa: " + resolvedAddr) : ("GPS: " + geo.lat + ", " + geo.lon)}\n\n` +
          `Soubor: ${kat}/${file}`
        );
        if (!ok) return;

        await apiFetch("/api/vyjezd/foto-location", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orderId, file, kategorie: kat, meta }),
        });

        
 
         
         // FOTO_VYJEZD_PROPIS_V2: volitelne propiseme adresu a cas do "vyjezdy" (primarni) + "vyjezd" (fallback)
         try {
           const vyjPayload = {
             savedFrom: "foto",
             address: resolvedAddr || addr || null,
             geo: geo || null,
             takenAt: meta?.takenAt || null,
             file,
             kategorie: kat,
           };

           // Volby uzivatele
           const doAddr = window.confirm(
             "Nastavit jako místo výjezdu:\n\n" +
             "Propsat ADRESU do Výjezdu?\n\n" +
             (vyjPayload.address ? ("Adresa: " + vyjPayload.address) : (vyjPayload.geo ? ("GPS: " + vyjPayload.geo.lat + ", " + vyjPayload.geo.lon) : "Bez adresy/GPS")) +
             "\n\nOK = ANO, Zrušit = NE"
           );

           let doTime = false;
           if (doAddr) {
             doTime = window.confirm(
               "Propsat i DATUM + ČAS výjezdu z fotky (takenAt)?\n\n" +
               (vyjPayload.takenAt ? ("takenAt: " + vyjPayload.takenAt) : "takenAt není k dispozici") +
               "\n\nOK = ANO, Zrušit = NE"
             );
           }

           // parse takenAt -> datum/cas
           let datum = "";
           let cas = "";
           if (doTime && vyjPayload.takenAt) {
             const d = new Date(vyjPayload.takenAt);
             if (!isNaN(d.getTime())) {
               const yyyy = String(d.getFullYear());
               const mm = String(d.getMonth() + 1).padStart(2, "0");
               const dd = String(d.getDate()).padStart(2, "0");
               const hh = String(d.getHours()).padStart(2, "0");
               const mi = String(d.getMinutes()).padStart(2, "0");
               datum = `${yyyy}-${mm}-${dd}`;
               cas = `${hh}:${mi}`;
             }
           }

           if (!doAddr) return;

           // uloz i do localStorage (fallback po refreshi)
           try { localStorage.setItem(`vyjezd_from_foto_${orderId}`, JSON.stringify(vyjPayload)); } catch {}

           const patchObj = (prev) => {
             const pv = prev || {};
             const prevVyjezdy = pv.vyjezdy || {};
             const prevVyjezd = pv.vyjezd || {};

             const nextVyjezdy = {
               ...prevVyjezdy,
               // adresa
               mistoUmrti: vyjPayload.address || prevVyjezdy.mistoUmrti || "",
               adresaUmrti: vyjPayload.address || prevVyjezdy.adresaUmrti || "",
               // gps
               geo: vyjPayload.geo || prevVyjezdy.geo || null,
               fotoMistoVyjezdu: vyjPayload,
             };

             // volitelne datum+cas
             if (datum) {
               nextVyjezdy.datumVyjezdu = datum;
               nextVyjezdy.datum_vyjezdu = datum;
             }
             if (cas) {
               nextVyjezdy.casVyjezdu = cas;
               nextVyjezdy.cas_vyjezdu = cas;
             }

             const nextVyjezd = {
               ...prevVyjezd,
               adresa: vyjPayload.address || prevVyjezd.adresa || "",
               geo: vyjPayload.geo || prevVyjezd.geo || null,
               fotoMistoVyjezdu: vyjPayload,
             };
             if (datum) { nextVyjezd.datumVyjezdu = datum; nextVyjezd.datum_vyjezdu = datum; }
             if (cas) { nextVyjezd.casVyjezdu = cas; nextVyjezd.cas_vyjezdu = cas; }

             return { ...pv, vyjezdy: nextVyjezdy, vyjezd: nextVyjezd };
           };

           // 1) pokud máš updateFormData(merge objekt)
           if (typeof ctx?.updateFormData === "function") {
             // updateFormData je merge – tak mu předáme celý vyjezdy objekt
             const next = patchObj(ctx?.formData || {});
             ctx.updateFormData({ vyjezdy: next.vyjezdy, vyjezd: next.vyjezd });
           }
           // 2) pokud máš setFormData(fn)
           else if (typeof ctx?.setFormData === "function") {
             ctx.setFormData((prev) => patchObj(prev));
           }
         } catch (e) {
           console.warn("FOTO_VYJEZD_PROPIS_V2 failed", e);
         }
setMsg("✅ Uloženo jako místo výjezdu.");
      } catch (e) {
        setMsg(`❌ Nastavení výjezdu selhalo: ${safeStr(e?.data?.error || e?.message || e)}`);
      }
    };
    // ===== /FOTO_SET_VYJEZD_V1 =====



  const handleDelete = async (it) => {
    const file = it?.filename || it?.file;
    const kat = it?.kategorie || "jine";
    if (!file) return;

    if (!confirm(`Smazat soubor?\n${kat}/${file}`)) return;

    setBusy(true);
    setMsg("");
    try {
      if (!hasRealId) {
        setMsg("❌ Chybí ID zakázky.");
        return;
      }

      await apiFetch(
        `/api/foto/delete?orderId=${encodeURIComponent(orderId)}&kategorie=${encodeURIComponent(kat)}&file=${encodeURIComponent(file)}`,
        { method: "DELETE" }
      );

      // drop preview
      const key = `${kat}/${file}`;
      setPreviews((prev) => {
        const copy = { ...(prev || {}) };
        if (copy[key]?.url) {
          try { URL.revokeObjectURL(copy[key].url); } catch {}
        }
        delete copy[key];
        return copy;
      });

      setMsg("✅ Smazáno.");
      await loadList();
    } catch (e) {
      setMsg(`❌ Mazání selhalo: ${safeStr(e?.data?.error || e?.message || e)}`);
    } finally {
      setBusy(false);
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const it of items) {
      const kat = it?.kategorie || "nezarazeno";
      if (!g[kat]) g[kat] = [];
      g[kat].push(it);
    }
    return g;
  }, [items]);

  return (
    <div style={{ padding: 16, maxWidth: 980 }}>
      <h2 style={{ fontSize: 22, fontWeight: 900, marginBottom: 8 }}>Foto dokumentace</h2>

      <div style={{ marginBottom: 12, padding: 10, border: "1px solid #ccc", borderRadius: 10, background: "#f5f5f5", fontSize: 13 }}>
        Zakázka ID: <b>{orderId || "—"}</b> • Server: <b>{API_BASE}</b>
        {!hasRealId ? (
          <div style={{ marginTop: 6, color: "#b00020", fontWeight: 900 }}>
            ⚠️ Nejdřív otevři/načti zakázku v Knize (chybí currentOrderId). Upload je zablokovaný.
          </div>
        ) : null}
      </div>

      <div style={cardStyle}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>➕ Nahrát fotky</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div>
            <div style={labelStyle}>Kategorie</div>
            <select value={kategorie} onChange={(e) => setKategorie(e.target.value)} style={inputStyle}>
              <option value="ohledaci_list">Ohledací list</option>
              <option value="zesnuly">Zesnulý</option>
              <option value="obrad">Obřad</option>
              <option value="kvetiny">Květiny</option>
              <option value="jine">Jiné</option>
            </select>
          </div>

          <div>
            <div style={labelStyle}>Vybrat soubory</div>
            <input
              type="file"
              accept="image/*,.pdf"
              multiple
              onChange={handleUpload}
              disabled={busy || !hasRealId}
              style={{ opacity: busy || !hasRealId ? 0.6 : 1 }} />
          </div>
        </div>

        <button onClick={loadList} disabled={busy} style={{ ...btnStyle, opacity: busy ? 0.7 : 1 }}>
          {busy ? "Pracuju…" : "Obnovit seznam"}
        </button>
      </div>

      <div style={cardStyle}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>📂 Přehled fotek</div>

        
          <button
            onClick={stahnoutZip}
            disabled={busy || !hasRealId}
            style={{ ...btnStyle, marginBottom: 10, opacity: busy || !hasRealId ? 0.6 : 1 }}
            title={!hasRealId ? "Nejdřív otevři zakázku v Knize (musí existovat ID)." : "Stáhnout všechny fotky jako ZIP"}
          >
            📦 Stáhnout fotodokumentaci (ZIP)
          </button>

          <button
            onClick={() => stahnoutZip(kategorie)}
            disabled={busy || !hasRealId}
            style={{ ...btnStyle, marginBottom: 10, opacity: busy || !hasRealId ? 0.6 : 1 }}
            title={!hasRealId ? "Nejdřív otevři zakázku v Knize (musí existovat ID)." : "Stáhnout ZIP jen pro zvolenou kategorii"}
          >
            📦 Stáhnout ZIP jen: {kategorie}
          </button>

{busy ? <div style={{ fontWeight: 900 }}>⏳ Načítám…</div> : null}

        {Object.keys(grouped).length === 0 ? (
          <div style={{ fontSize: 13, opacity: 0.85 }}>Zatím tu nejsou žádné soubory.</div>
        ) : (
          Object.entries(grouped).map(([kat, arr]) => (
            <div key={kat} style={{ marginBottom: 14 }}>
              <div style={{ fontWeight: 900, marginBottom: 8 }}>{kat}</div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 10 }}>
                {arr.map((it) => {
                  const file = it?.filename || it?.file || "";
                  const kat2 = it?.kategorie || kat || "nezarazeno";
                  const key = `${kat2}/${file}`;
                  const isImg = isImageName(file);
                  const pv = previews[key];

                    // VYJEZD_BTN_ANYCAT_v1
                    const metaX = metaByKey[key] || null;
                    const hasLoc =
                      !!(metaX && (
                        (metaX.address || metaX.addr) ||
                        (metaX.geo && metaX.geo.lat != null && metaX.geo.lon != null)
                      ));
                    // /VYJEZD_BTN_ANYCAT_v1


                  const downloadUrl = `${HTTPS_API_BASE}/api/foto/file?orderId=${encodeURIComponent(orderId)}&kategorie=${encodeURIComponent(kat2)}&file=${encodeURIComponent(file)}`;

                  return (
                    <div key={key} style={{ border: "1px solid #ddd", borderRadius: 10, padding: 10 }}>
                        {file ? (
                        isImg ? (
                          pv?.url ? (
                            <a
                              href={downloadUrl}
                              download={file}
                              target="_blank"
                              rel="noreferrer"
                              style={{ display: "block" }}
                              title="Otevřít (stáhne/otevře přes API)"
                            >
                              <img
                                src={pv?.url || ""}
                                alt={file}
                                style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 8 }}
                              />
                            
                          {/* VYJEZD_FROM_FOTO_BTN_V1 */}
                          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                            {hasLoc ? (
                              <button
                                onClick={() => setAsVyjezdLocation(it)}
                                disabled={busy || !hasRealId}
                                style={{ ...btnStyle, padding: "8px 10px", fontSize: 12, opacity: busy || !hasRealId ? 0.6 : 1 }}
                                title={!hasRealId ? "Nejdřív otevři zakázku v Knize (musí existovat ID)." : "Nastavit místo výjezdu podle GPS/adresy z fotky"}
                              >
                                📍 Nastavit místo výjezdu
                              </button>
                            ) : (
                              <button
                                onClick={async () => {
                                  try {
                                    if (!it?.kategorie || !(it?.file || it?.filename)) return;
                                    const katX = it.kategorie;
                                    const fileX = it.file || it.filename;
                                    await fetchMeta(katX, fileX);
                                    alert("Meta načtena. Pokud fotka obsahuje GPS, tlačítko výjezdu se zpřístupní.");
                                  } catch (e) {}
                                }}
                                disabled={busy || !hasRealId}
                                style={{ ...btnStyle, padding: "8px 10px", fontSize: 12, opacity: busy || !hasRealId ? 0.6 : 1 }}
                                title="Zkusit načíst meta (čas/GPS) z této fotky"
                              >
                                🛰️ Načíst meta (GPS)
                              </button>
                            )}
                          </div>
                          {/* /VYJEZD_FROM_FOTO_BTN_V1 */}
</a>
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: 140,
                                borderRadius: 8,
                                background: "#eee",
                                display: "grid",
                                placeItems: "center",
                                fontWeight: 900,
                              }}
                            >
                              Náhled…
                            </div>
                          )
                        ) : (
                          <>
                            <div
                              onClick={() => { if (pv?.url) setLightbox({ src: pv.url, file }); }}
                              style={{ cursor: "zoom-in" }}
                            >
                              <img
                                src={pv?.url || ""}
                                alt={file}
                                style={{ width: "100%", height: 140, objectFit: "cover", borderRadius: 8 }}
                              />
                            </div>
                      
                            <a
                              href={downloadUrl}
                              target="_blank"
                              rel="noreferrer"
                              style={{ display: "block", marginTop: 6, fontWeight: 900, textAlign: "center" }}
                            >
                              Stáhnout
                            </a>
                          </>
                        )
                      ) : (
                        <div style={{ fontWeight: 900 }}>Soubor neznámý</div>
                      )}

<div style={{ fontSize: 12, marginTop: 8, wordBreak: "break-word" }}>
                        <b>{file}</b>
                      </div>

  {/* FOTO_ADDR_UI_V1 */}
  {(() => {
    const meta = metaByKey[key];
    const addr = meta?.address || meta?.addr || null;
    const geo = meta?.geo || null;
    if (!addr && !(geo && geo.lat != null && geo.lon != null)) return null;
    return (
      <div style={{ marginTop: 4, fontSize: 11, opacity: 0.9 }}>
        {addr ? (
          <div>📍 {addr}</div>
        ) : (
          <div>🧭 {String(geo.lat).slice(0, 10)}, {String(geo.lon).slice(0, 10)} (±{geo?.acc ? Math.round(Number(geo.acc)) : "?"} m)</div>
        )}
      </div>
    );
  })()}


                      
  {hasLoc ? (
    <button
      onClick={() => setAsVyjezdLocation(it)}
      disabled={busy || !hasRealId}
      style={{ ...btnStyle, marginTop: 8, width: "100%", opacity: busy || !hasRealId ? 0.7 : 1 }}
      title="Uloží adresu/GPS z této fotky jako místo výjezdu (po potvrzení)."
    >
      📍 Nastavit jako místo výjezdu
    </button>
  ) : null}

<button
                        onClick={() => handleDelete(it)}
                        disabled={busy || !hasRealId}
                        style={{ ...btnStyle, marginTop: 8, width: "100%", background: "#fff", opacity: busy || !hasRealId ? 0.7 : 1 }}
                      >
                        Smazat
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {msg ? (
        <pre style={{ marginTop: 12, fontWeight: 700, whiteSpace: "pre-wrap", background: "#111", color: "#fff", padding: 12, borderRadius: 10 }}>
          {msg}
        </pre>
      ) : null}
{lightbox && (
  <div
    onClick={() => setLightbox(null)}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.85)",
      display: "grid",
      placeItems: "center",
      zIndex: 9999,
    }}
  >
    <img
        src={lightbox?.src || ""}
        alt={lightbox.file}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 12 }}
      />
  </div>
)}
  
  </div>
  );
}
